import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  estEncoreCorrigeable,
  useAnnuaire,
  useCorrigerUnMessage,
  useEnvoyerUnMessage,
  useFil,
  usePoserLaLecture,
  usePoserLaNotification,
  usePoserUneReaction,
  useRetirerUnMessage,
} from '../features/conversations/conversations';
import { lireLErreur } from '../shared/api/client';
import { Bandeau } from '../shared/ui/Bandeau';
import { Bouton } from '../shared/ui/Bouton';
import { Carte } from '../shared/ui/Carte';
import { EtatErreur } from '../shared/ui/EtatErreur';
import { EtatVide } from '../shared/ui/EtatVide';
import { LigneDePersonne } from '../shared/ui/LigneDePersonne';
import { Pastille } from '../shared/ui/Pastille';
import { RangeeDeReactions } from '../shared/ui/RangeeDeReactions';
import { Saisie } from '../shared/ui/Champ';
import { Squelette } from '../shared/ui/Squelette';
import { Titre } from '../shared/ui/Titre';
import { ZoneDeTexte } from '../shared/ui/ZoneDeTexte';
import { instantLisible } from '../shared/format/duree';
import { nomAffiche } from '../shared/format/designation';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ANNUAIRE — `CDC-4` F-50, ET C'EST **UNE** SURFACE, PAS DEUX.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « **Une seule surface, pas deux.** Le produit ne met en relation que des personnes avec
 * qui on peut parler : **deux listes porteraient exactement les mêmes lignes.** » Cet écran
 * est donc **l'annuaire ET les conversations**, et il n'existe nulle part un second
 * inventaire.
 *
 * « **La bulle du chat est présente partout et tout le temps, et cette surface est ce
 * qu'elle ouvre en grand.** » La liste et le fil se montrent donc **côte à côte** : ouvrir
 * le fil dans un autre écran ferait perdre la liste, qui est ce depuis quoi on circule.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QU'UNE LIGNE PORTE, ET CE QU'ELLE NE PORTE PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Elle porte « le prénom du ou des correspondants, **leur état de présence**, le dernier
 * message et son instant, et le nombre de non-lus ». Et **les discussions de groupe y
 * figurent comme les autres**, « avec les prénoms de leurs correspondants, **et elles ne
 * portent aucun nom** » — il n'y a donc aucun titre à afficher, et rien à saisir pour en
 * donner un.
 *
 * **Il n'y a PAS de fiche d'une personne** : « une ligne, et rien derrière — le produit ne
 * montre jamais d'un tiers plus que son prénom, **donc une fiche n'aurait rien à
 * porter** ». Cliquer une ligne ouvre le fil, et rien d'autre ne s'ouvre.
 *
 * **La recherche porte sur ses propres conversations seulement**, et elle se fait ici, en
 * mémoire : la liste des relations d'une personne est bornée par ses rattachements, donc
 * elle tient. Une recherche serveur supposerait une liste qui ne tient pas.
 */
export function PageAnnuaire() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const annuaire = useAnnuaire();
  const [filtre, poserFiltre] = useState('');

  const lignes = (annuaire.data?.lignes ?? []).filter((ligne) => {
    if (filtre.trim() === '') return true;
    const cherche = filtre.trim().toLowerCase();
    return ligne.correspondants.some((personne) =>
      (personne.prenom ?? '').toLowerCase().includes(cherche),
    );
  });

  return (
    <main className="mx-auto flex w-full max-w-espace flex-col gap-6 p-6">
      <Titre niveau={1}>Conversations</Titre>

      {annuaire.isLoading && <Squelette lignes={5} />}

      {annuaire.isError && (
        <EtatErreur
          message={lireLErreur(annuaire.error).message}
          onReessayer={() => void annuaire.refetch()}
        />
      )}

      {annuaire.data !== undefined && annuaire.data.lignes.length === 0 && (
        // **Le deuxième des trois états vides de F-54** : « aucune relation ». Il dit ce
        // qu'il faut faire, et ce qu'il faut faire est **hors du produit** — « une
        // personne qui a un problème passe par la structure qui lui a donné son accès ».
        <EtatVide titre="Aucune relation">
          Vos conversations apparaîtront ici dès qu’une structure vous mettra en
          relation avec un tuteur ou un élève.
        </EtatVide>
      )}

      {annuaire.data !== undefined && annuaire.data.lignes.length > 0 && (
        <div className="grid gap-4 md:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
          <div className="flex flex-col gap-3">
            <Saisie
              libelle="Rechercher"
              type="search"
              value={filtre}
              onChange={(evenement) => poserFiltre(evenement.target.value)}
            />

            {lignes.length === 0 ? (
              <p className="text-14 text-texte-faible">
                Aucune conversation ne correspond.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {lignes.map((ligne) => (
                  <li key={ligne.conversationId}>
                    <LigneDeConversation
                      ligne={ligne}
                      ouverte={ligne.conversationId === conversationId}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {conversationId === undefined ? (
            <Carte>
              <p className="text-14 text-texte-faible">
                Choisissez une conversation pour la lire.
              </p>
            </Carte>
          ) : (
            <Fil conversationId={conversationId} />
          )}
        </div>
      )}
    </main>
  );
}

/**
 * **Le refus d'une correction, dit en français.**
 *
 * `DOCTRINE.md` §5 : « **`code` est le contrat réel** : c'est sur lui que le front branche
 * son comportement, **jamais sur `message`** ». Les deux codes que F-40 nomme à ses
 * critères d'acceptation sont donc traduits ici, et tout autre refus garde le message du
 * serveur plutôt qu'une phrase inventée qui dirait la mauvaise cause.
 */
function refusDeCorrection(erreur: unknown): string {
  const refus = lireLErreur(erreur);
  if (refus.code === 'DELAI_DE_CORRECTION_DEPASSE') {
    return 'Passé cinq minutes, le texte d’un message ne se change plus.';
  }
  if (refus.code === 'MESSAGE_D_UN_AUTRE') {
    return 'Personne ne corrige le message d’un autre.';
  }
  return refus.message;
}

type LigneDAnnuaire = NonNullable<
  ReturnType<typeof useAnnuaire>['data']
>['lignes'][number];

/**
 * **Une ligne de l'annuaire.**
 *
 * **Le lien porte l'identifiant de la CONVERSATION, pas celui d'une personne** : sur une
 * discussion de groupe il y a plusieurs correspondants et une seule conversation, et
 * « les discussions de groupe y figurent **comme les autres lignes** ».
 */
function LigneDeConversation({
  ligne,
  ouverte,
}: {
  readonly ligne: LigneDAnnuaire;
  readonly ouverte: boolean;
}) {
  const prenoms = ligne.correspondants
    .map((personne) => nomAffiche(personne.prenom))
    .join(', ');
  // **Une seule pastille de présence par ligne**, et sur un groupe elle prend l'état le
  // plus « présent » : montrer trois pastilles pour trois correspondants ferait lire une
  // ligne comme un tableau, et DD-07 ferme déjà tout voyant de qualité de liaison.
  const etat = ligne.correspondants.some((p) => p.etat === 'connecte')
    ? 'connecte'
    : ligne.correspondants.some((p) => p.etat === 'occupe')
      ? 'occupe'
      : 'absent';

  return (
    <Link
      to={`/annuaire/${ligne.conversationId}`}
      aria-current={ouverte ? 'page' : undefined}
      className={[
        'block rounded-3 border p-3 transition-colors duration-1',
        ouverte
          ? 'border-bord-fort bg-fond-bloc'
          : 'border-bord bg-fond-surface hover:bg-fond-attenue',
      ].join(' ')}
    >
      <LigneDePersonne
        prenom={prenoms}
        etat={etat}
        action={
          ligne.nonLus > 0 ? (
            <span className="inline-flex min-w-6 items-center justify-center rounded-plein bg-action px-2 text-12 font-weight-700 text-action-texte">
              {ligne.nonLus}
            </span>
          ) : undefined
        }
      />
      {ligne.dernierMessage !== null && (
        <p className="mt-1 truncate text-13 text-texte-faible">
          {ligne.dernierMessage.deMoi && 'Vous : '}
          {ligne.dernierMessage.texte === null
            ? ligne.dernierMessage.porteDesFichiers
              ? 'Des fichiers'
              : 'Message retiré'
            : ligne.dernierMessage.texte}
        </p>
      )}
    </Link>
  );
}

/**
 * **Le fil d'une conversation.**
 *
 * **Un message porte trois faits** : présent, corrigé ou retiré. « Un message retiré garde
 * **sa marque sans son contenu** » — la ligne reste, vidée, et c'est ce qui empêche de
 * réécrire ce que l'autre a vu « dans la seule surface du produit que personne ne peut
 * auditer ».
 */
function Fil({ conversationId }: { readonly conversationId: string }) {
  const fil = useFil(conversationId);
  const envoi = useEnvoyerUnMessage(conversationId);
  const lecture = usePoserLaLecture(conversationId);
  const notification = usePoserLaNotification(conversationId);
  const retrait = useRetirerUnMessage(conversationId);
  const correction = useCorrigerUnMessage(conversationId);
  const reaction = usePoserUneReaction(conversationId);
  const [texte, poserTexte] = useState('');
  // **Un seul message se corrige à la fois**, et l'édition se fait EN PLACE : ouvrir la
  // correction ailleurs ferait perdre le fil, qui est le contexte de ce qu'on réécrit.
  const [enCorrection, poserEnCorrection] = useState<string | null>(null);
  const [brouillon, poserBrouillon] = useState('');

  /**
   * **L'horloge bat pour que le bouton « Corriger » PARTE À L'HEURE.**
   *
   * La fenêtre de Q461 se ferme pendant qu'on regarde l'écran. Sans ce battement, le
   * bouton resterait affiché jusqu'au prochain rendu, c'est-à-dire jusqu'à un événement
   * sans rapport — et il ne pourrait alors plus que refuser, ce qu'on cherche justement
   * à éviter. Quinze secondes suffisent : la fenêtre dure cinq minutes.
   */
  const [maintenant, poserMaintenant] = useState(() => Date.now());
  useEffect(() => {
    const battement = window.setInterval(() => {
      poserMaintenant(Date.now());
    }, 15_000);
    return () => {
      window.clearInterval(battement);
    };
  }, []);

  // **La pagination remonte vers le passé, donc les tranches arrivent à l'envers.**
  const messages = [...(fil.data?.pages ?? [])].reverse().flatMap((t) => t.messages);
  const dernierRang = messages.at(-1)?.rang ?? null;

  // **La lecture se pose quand le dernier message change, et pas à chaque rendu.** Le
  // serveur ignore un rang qui recule ; poser à chaque rendu ferait juste du bruit réseau.
  useEffect(() => {
    if (dernierRang !== null) lecture.mutate(dernierRang);
    // `lecture` est une mutation stable côté TanStack Query, et l'ajouter aux dépendances
    // relancerait l'effet à chaque changement d'état de la mutation — donc en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dernierRang, conversationId]);

  return (
    <Carte>
      <div className="flex h-full flex-col gap-3">
        {fil.isLoading && <Squelette lignes={5} />}

        {fil.isError && (
          <EtatErreur
            message={lireLErreur(fil.error).message}
            onReessayer={() => void fil.refetch()}
          />
        )}

        {fil.hasNextPage && (
          <Bouton
            variante="fantome"
            taille="petite"
            enCours={fil.isFetchingNextPage}
            onClick={() => void fil.fetchNextPage()}
          >
            Voir les messages précédents
          </Bouton>
        )}

        <ul className="flex flex-col gap-3">
          {messages.map((message) => (
            <li
              key={message.id}
              className={[
                'flex flex-col gap-1 rounded-3 p-3',
                message.deMoi ? 'bg-fond-selection' : 'bg-fond-attenue',
              ].join(' ')}
            >
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="text-13 font-weight-700 text-texte-fort">
                  {nomAffiche(message.auteur.prenom)}
                </span>
                <time
                  dateTime={message.instant}
                  className="text-12 text-texte-faible"
                >
                  {instantLisible(message.instant)}
                </time>
                {/* **« Corrigé » se dit, et le contenu d'avant ne se montre pas.** */}
                {message.corrige && (
                  <span className="text-12 text-texte-faible">corrigé</span>
                )}
              </span>

              {message.retire ? (
                // « **Un message retiré garde sa marque sans son contenu.** »
                <p className="text-14 italic text-texte-faible">
                  Message retiré.
                </p>
              ) : (
                <>
                  {message.texte !== null && (
                    <p className="whitespace-pre-wrap text-14 text-texte">
                      {message.texte}
                    </p>
                  )}
                  {message.fichiers.length > 0 && (
                    <ul className="flex flex-col gap-1">
                      {message.fichiers.map((fichier) => (
                        <li
                          key={fichier.id}
                          className="flex items-center gap-2 text-13 text-texte-faible"
                        >
                          <Pastille teinte="neutre" pleine={false} />
                          {fichier.nom}
                        </li>
                      ))}
                    </ul>
                  )}
                  {enCorrection === message.id ? (
                    <form
                      className="flex flex-col gap-2"
                      onSubmit={(evenement) => {
                        evenement.preventDefault();
                        // « Corriger, c'est REMPLACER un texte. Vider un message n'est pas
                        // une correction, c'est un RETRAIT », et il a son propre bouton.
                        if (brouillon.trim() === '') return;
                        correction.mutate(
                          { messageId: message.id, texte: brouillon },
                          { onSuccess: () => poserEnCorrection(null) },
                        );
                      }}
                    >
                      <ZoneDeTexte
                        libelle="Corriger votre message"
                        libelleCache
                        rows={2}
                        value={brouillon}
                        onChange={(evenement) =>
                          poserBrouillon(evenement.target.value)
                        }
                      />
                      <span className="flex flex-wrap items-center gap-2">
                        <Bouton
                          type="submit"
                          taille="petite"
                          enCours={correction.isPending}
                        >
                          Enregistrer
                        </Bouton>
                        <Bouton
                          variante="fantome"
                          taille="petite"
                          onClick={() => {
                            correction.reset();
                            poserEnCorrection(null);
                          }}
                        >
                          Annuler
                        </Bouton>
                      </span>
                      {correction.isError && (
                        <Bandeau ton="echec">
                          {refusDeCorrection(correction.error)}
                        </Bandeau>
                      )}
                    </form>
                  ) : (
                    message.deMoi && (
                      <span className="flex flex-wrap items-center gap-2">
                        {/* **« Corriger » N'EXISTE QUE TANT QU'IL PEUT ABOUTIR, et c'est
                            le choix fait ici.** Q384 veut que « le produit explique ses
                            refus » ; un bouton qui ne peut plus que refuser n'explique
                            rien, il propose. Le laisser bloqué sous chacun de ses
                            messages, pour toujours, poserait en plus un objet mort sur
                            toute la hauteur du fil — la fenêtre dure cinq minutes, le
                            reste du fil est passé. **Le refus reste expliqué là où il
                            peut encore se produire** : la fenêtre peut se fermer entre
                            l'ouverture de l'édition et l'envoi, et le serveur répond
                            alors DELAI_DE_CORRECTION_DEPASSE, que le bandeau ci-dessus
                            dit en clair. */}
                        {estEncoreCorrigeable(message.instant, maintenant) && (
                          <Bouton
                            variante="fantome"
                            taille="petite"
                            onClick={() => {
                              correction.reset();
                              poserBrouillon(message.texte ?? '');
                              poserEnCorrection(message.id);
                            }}
                          >
                            Corriger
                          </Bouton>
                        )}
                        <Bouton
                          variante="fantome"
                          taille="petite"
                          enCours={retrait.isPending}
                          onClick={() => retrait.mutate(message.id)}
                        >
                          Retirer
                        </Bouton>
                      </span>
                    )
                  )}

                  {/* **On réagit AUSSI au message d'un autre, et surtout au sien** : la
                      rangée est sous tous les messages présents. Elle n'est pas sous un
                      message retiré — « la ligne reste, VIDÉE », et il n'y a plus rien à
                      quoi réagir ; le corpus ne tranche pas ce cas, le motif est écrit
                      dans le rapport de tâche. */}
                  <RangeeDeReactions
                    reactions={message.reactions}
                    enCours={reaction.isPending}
                    onPoser={(valeur) =>
                      reaction.mutate({ messageId: message.id, valeur })
                    }
                  />
                </>
              )}
            </li>
          ))}
        </ul>

        {/* **L'exception de notification se règle ICI**, F-51 : « dans la conversation,
            pas dans les réglages », qui n'en portent que le nombre. */}
        <div className="flex flex-wrap items-center gap-2 border-t border-bord pt-3">
          <span className="text-13 text-texte-faible">
            Emails pour cette conversation&nbsp;:
          </span>
          <Bouton
            variante="fantome"
            taille="petite"
            enCours={notification.isPending}
            onClick={() => notification.mutate(true)}
          >
            Toujours
          </Bouton>
          <Bouton
            variante="fantome"
            taille="petite"
            enCours={notification.isPending}
            onClick={() => notification.mutate(false)}
          >
            Jamais
          </Bouton>
          <Bouton
            variante="fantome"
            taille="petite"
            enCours={notification.isPending}
            onClick={() => notification.mutate(null)}
          >
            Suivre mon réglage général
          </Bouton>
        </div>

        <form
          className="flex items-end gap-2 border-t border-bord pt-3"
          onSubmit={(evenement) => {
            evenement.preventDefault();
            // « **Un message ne naît jamais vide** — sans texte ni fichier, l'envoi est
            // refusé. » Le serveur refuse ; ce test-ci évite d'envoyer pour rien.
            if (texte.trim() === '') return;
            envoi.mutate(texte, { onSuccess: () => poserTexte('') });
          }}
        >
          <ZoneDeTexte
            libelle="Votre message"
            libelleCache
            rows={2}
            value={texte}
            onChange={(evenement) => poserTexte(evenement.target.value)}
          />
          <Bouton type="submit" enCours={envoi.isPending}>
            Envoyer
          </Bouton>
        </form>

        {envoi.isError && (
          <Bandeau ton="echec">{lireLErreur(envoi.error).message}</Bandeau>
        )}
      </div>
    </Carte>
  );
}
